<template>
    <div
        class="l-ss-c-section-categories"
        ref="categoriesContainer">
        <!-- Скрытый контейнер для измерения всех тегов -->
        <div
            class="l-ss-c-section-categories-measure"
            ref="measureContainer"
            style="position: absolute; visibility: hidden;">
            <section-categories-tag
                v-for="category in categoryFilterOptions.slice(0, MAX_VISIBLE_TAGS)"
                :key="category.id"
                :category="category"/>
        </div>

        <section-categories-tag
            v-for="category in displayedCategories"
            :key="category.id"
            :category="category"
            :active-categories="activeCategories"
            @toggle-active="toggleActiveCategory"/>

        <div
            v-if="isShowMoreButton"
            class="l-ss-c-section-categories-more-btn"
            ref="moreButton"
            @click="toggleShowAll">
            {{ showAll ? 'Скрыть' : 'Еще' }}
        </div>
    </div>
</template>

<script>
    import SectionCategoriesTag from '@/core/components/section-results/section-categories/section-categories-tag.js';

    import { computed, watch, ref, onMounted, onUnmounted, nextTick } from '@vue/composition-api';

    export default {
        components: {
            SectionCategoriesTag,
        },
        props: {
            filters: {
                type: Array,
            },
            activeFilters: {
                type: Array,
            },
        },
        setup(props, { emit, parent }) {
            const MAX_VISIBLE_TAGS = 7;
            const TAG_MARGIN = 9; // отступ между тегами

            const isMobile = parent.$screen.isMobile;

            const categoriesContainer = ref(null);
            const measureContainer = ref(null);
            const moreButton = ref(null);
            const visibleCategoriesCount = ref(MAX_VISIBLE_TAGS);
            const showAll = ref(false);

            const categoryFilterOptions = computed(() => {
                const options = props.filters.find(filter => filter.id === 'category')?.options;

                if (!options) {
                    return [];
                }

                const selectedIndexMap = new Map(activeCategories.value.map((id, index) => [id, index]));

                options.sort((a, b) => {
                    let indexA = selectedIndexMap.get(a.id);
                    let indexB = selectedIndexMap.get(b.id);

                    if (indexA === undefined) {
                        indexA = Infinity;
                    }

                    if (indexB === undefined) {
                        indexB = Infinity;
                    }

                    return indexA - indexB;
                });

                return [{ id: 'all', name: 'Все товары' }, ...options];
            });

            const visibleCategories = computed(() => {
                return categoryFilterOptions.value.slice(0, visibleCategoriesCount.value);
            });

            const activeCategories = computed(() => {
                return props.activeFilters.find(filter => filter.field === 'category')?.options || [];
            });

            const toggleShowAll = () => {
                showAll.value = !showAll.value;
                emit('toggle-categories', showAll.value ? 'Еще' : 'Скрыть');
                if (!showAll.value) {
                    nextTick(updateVisibleCategories);
                }
            };

            const displayedCategories = computed(() => {
                return showAll.value ? categoryFilterOptions.value : visibleCategories.value;
            });

            const isShowMoreButton = computed(() => {
                return displayedCategories.value.length < categoryFilterOptions.value.length || showAll.value;
            });

            const updateVisibleCategories = () => {
                if (isMobile) {
                    visibleCategoriesCount.value = MAX_VISIBLE_TAGS;
                    return;
                }

                if (!categoriesContainer.value || !measureContainer.value) return;

                const container = categoriesContainer.value;
                const containerWidth = container.offsetWidth;
                const moreButtonWidth = isShowMoreButton.value ? moreButton.value.offsetWidth : 0;
                let availableWidth = containerWidth - moreButtonWidth - (2 * TAG_MARGIN); // вычитаем отступ для кнопки
                let totalWidth = 0;
                let count = 0;

                const items = measureContainer.value.querySelectorAll('.l-ss-c-section-categories-tag');

                for (let i = 0; i < Math.min(items.length, MAX_VISIBLE_TAGS); i++) {
                    const item = items[i];
                    // Добавляем ширину тега и отступ справа
                    const itemFullWidth = item.offsetWidth + TAG_MARGIN;

                    if (totalWidth + itemFullWidth > availableWidth) break;

                    totalWidth += itemFullWidth;
                    count++;
                }

                visibleCategoriesCount.value = Math.max(1, count);
            };

            watch(() => props.filters, () => {
                nextTick(updateVisibleCategories);
            });

            const toggleActiveCategory = ({ category, isActive }) => {
                const data = {
                    param: {
                        field: 'category',
                    },
                    categoryName: category.name,
                    isActive,
                };

                if (category.id !== 'all') {
                    let activeCategoryIds = [...activeCategories.value];

                    if (isActive) {
                        activeCategoryIds.push(category.id);
                    } else {
                        activeCategoryIds = activeCategoryIds.filter(id => id !== category.id);
                    }

                    if (activeCategoryIds.length) {
                        data.param.condition = 'in';
                        data.param.options = activeCategoryIds;
                    }
                }

                emit('toggle-active-category', data);
            };

            onMounted(() => {
                if (!isMobile) {
                    window.addEventListener('resize', updateVisibleCategories);
                }
                updateVisibleCategories();
            });

            onUnmounted(() => {
                window.removeEventListener('resize', updateVisibleCategories);
            });

            return {
                categoriesContainer,
                measureContainer,
                moreButton,
                displayedCategories,
                showAll,
                isShowMoreButton,
                categoryFilterOptions,
                activeCategories,
                MAX_VISIBLE_TAGS,
                toggleShowAll,
                toggleActiveCategory,
            };
        },
    };
</script>
